/**
 * Credits API
 *
 * Client for all credit-wallet endpoints on the Django auth service:
 *   - Balance, catalog, ledger (read)
 *   - Stripe checkout session creation (flat SKUs and legacy credit packs)
 *   - Legacy bundle cancellation
 *
 * Read the canonical product contract in docs/product/credit-actions-model.md before modifying.
 *
 * Uses apiClient (VITE_API_BASE_URL / FastAPI gateway, JWT injection, 401 refresh) via
 * the gateway proxy. The credit endpoints are owner-scoped;
 * the JWT carries the identity. Gateway proxies to Django.
 */

import { apiClient } from './client';

// Request / response types

export interface CreditPackCheckoutRequest {
  stripe_price_id: string;
  credit_amount: number;
  success_url?: string;
  cancel_url?: string;
}

export interface CreditBundleCheckoutRequest {
  stripe_price_id: string;
  credit_amount: number;
  success_url?: string;
  cancel_url?: string;
}

export interface CheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
}

export interface BuyerDecodeDeliveryStatus {
  checkout_session_id: string;
  property_id: string;
  valuation_ref: string;
  status: 'pending' | 'delivered' | 'refunded' | 'failed';
}

export interface BuyerDecodeCheckoutRequest {
  valuation_ref: string;
  inventory_revision: string;
  price_snapshot_ref: string;
  price_region: string;
  price_window_months: 12 | 24 | 36;
  success_url: string;
  cancel_url: string;
}

export interface CreditBalance {
  balance: number;
}

export interface CreditActionPrice {
  action_key: string;
  display_label: string;
  credit_cost: number;
}

export interface CreditCatalog {
  actions: CreditActionPrice[];
}

export interface FlatSkuPrice {
  unit_amount: number;
  currency: string;
  interval?: string | null;
  stripe_price_id: string;
}

export interface FlatSku {
  sku_key: string;
  display_label: string;
  description: string;
  teaser: string;
  checkout_mode: 'payment' | 'subscription';
  grant_type: 'none' | 'decode' | 'plot_package' | 'portfolio' | 'buyer_decode';
  max_properties: number | null;
  entitlement_days: number | null;
  price: FlatSkuPrice;
}

export type FlatSkuCatalog =
  | {
      available: true;
      skus: FlatSku[];
    }
  | {
      available: false;
      skus: [];
      error_code: 'flat_sku_billing_disabled' | 'commerce_disabled';
      detail: string;
    };

export interface FlatSkuCheckoutRequest {
  sku_key: string;
  property_id?: string;
  property_ids?: string[];
  success_url?: string;
  cancel_url?: string;
}

/**
 * Canonical ledger source values — mirrors CreditLedgerEntry.SOURCE_* constants
 * in backend_django/users/models_credits.py (all lowercase).
 */
export type LedgerSource = 'action' | 'purchase' | 'admin';

export interface CreditLedgerEntry {
  id: string;
  direction: 'debit' | 'grant' | 'refund';
  amount: number;
  action_key: string;
  property_id: string;
  idempotency_key: string;
  source: LedgerSource;
  balance_after: number;
  reverses: string | null;
  is_admin_bypass: boolean;
  note: string;
  created_at: string;
}

export interface CreditLedger {
  entries: CreditLedgerEntry[];
  next_cursor?: string | null;
  limit?: number;
  has_more?: boolean;
}

// API surface

const BASE = '/api/users/credits';

interface FlatSkuErrorData {
  code?: string;
  error_code?: string;
  detail?: string | { code?: string; error_code?: string; detail?: string };
}

const FLAT_SKU_DISABLED_CODE = 'flat_sku_billing_disabled';
const FLAT_SKU_DISABLED_DETAIL = 'Flat-SKU checkout is not enabled.';

function isFlatSkuBillingDisabledResponse(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: FlatSkuErrorData } }).response;
  if (response?.status !== 503) return false;

  const data = response.data;
  const nestedDetail = typeof data?.detail === 'object' ? data.detail : undefined;
  const detailText = typeof data?.detail === 'string' ? data.detail : nestedDetail?.detail;

  const codes = [data?.error_code, data?.code, nestedDetail?.error_code, nestedDetail?.code];
  return (
    codes.some((code) => code === FLAT_SKU_DISABLED_CODE) || detailText === FLAT_SKU_DISABLED_DETAIL
  );
}

function isFlatSkuCatalogUnavailableResponse(error: unknown): boolean {
  const status = (
    error as {
      response?: {
        status?: number;
      };
    }
  ).response?.status;

  return status === 503 || isFlatSkuBillingDisabledResponse(error);
}

export const CreditsAPI = {
  /** Current credit balance for the authenticated owner. Unmetered. */
  async getBalance(): Promise<CreditBalance> {
    const response = await apiClient.get<CreditBalance>(`${BASE}/balance/`);
    return response.data;
  },

  /** Data-driven catalog of active priced decision actions. */
  async getCatalog(): Promise<CreditCatalog> {
    const response = await apiClient.get<CreditCatalog>(`${BASE}/catalog/`);
    // The declared type is a compile-time claim about an untrusted wire body.
    // A catalog without `actions` would reach consumers and crash them on
    // `.actions.find(...)`, white-screening the Implement route. Reject the
    // body instead — an explicit failure the caller renders as unavailable,
    // never a substituted empty catalog (that would silently price actions
    // as unavailable and hide a real outage).
    if (!Array.isArray(response.data?.actions)) {
      throw new Error('Credit action catalog response is missing its "actions" list.');
    }
    return response.data;
  },

  /** Owner-facing flat SKU catalog with Stripe-sourced prices. */
  async getFlatSkus(): Promise<FlatSkuCatalog> {
    const response = await apiClient.get<FlatSkuCatalog>(`${BASE}/skus/`, {
      skipErrorLogging: isFlatSkuCatalogUnavailableResponse,
    });
    return response.data;
  },

  /** Append-only ledger history for the authenticated owner, newest first. */
  async getLedger(params?: { limit?: number; cursor?: string | null }): Promise<CreditLedger> {
    const response = await apiClient.get<CreditLedger>(`${BASE}/ledger/`, {
      params: {
        limit: params?.limit,
        cursor: params?.cursor || undefined,
      },
    });
    return response.data;
  },

  /** POST /api/users/credits/checkout/pack/ */
  async checkoutCreditPack(req: CreditPackCheckoutRequest): Promise<CheckoutSessionResponse> {
    const response = await apiClient.post<CheckoutSessionResponse>(`${BASE}/checkout/pack/`, req);
    return response.data;
  },

  /** POST /api/users/credits/checkout/bundle/ */
  async checkoutBundle(req: CreditBundleCheckoutRequest): Promise<CheckoutSessionResponse> {
    const response = await apiClient.post<CheckoutSessionResponse>(`${BASE}/checkout/bundle/`, req);
    return response.data;
  },

  /** POST /api/users/credits/checkout/sku/ */
  async checkoutFlatSku(req: FlatSkuCheckoutRequest): Promise<CheckoutSessionResponse> {
    const response = await apiClient.post<CheckoutSessionResponse>(`${BASE}/checkout/sku/`, req);
    return response.data;
  },

  async checkoutBuyerDecode(
    propertyId: string,
    req: BuyerDecodeCheckoutRequest,
  ): Promise<CheckoutSessionResponse> {
    const response = await apiClient.post<CheckoutSessionResponse>(
      `/api/v1/valuation/property/${encodeURIComponent(propertyId)}/buyer-decode/checkout`,
      req,
    );
    return response.data;
  },

  async getBuyerDecodeDeliveryStatus(
    checkoutSessionId: string,
  ): Promise<BuyerDecodeDeliveryStatus> {
    const response = await apiClient.get<BuyerDecodeDeliveryStatus>(
      `${BASE}/checkout/buyer-decode/${encodeURIComponent(checkoutSessionId)}/`,
    );
    return response.data;
  },

  /** POST /api/users/credits/checkout/cancel-bundle/ */
  async cancelBundle(propertyId?: string): Promise<{ cancelled: boolean; message: string }> {
    const response = await apiClient.post<{
      cancelled: boolean;
      message: string;
    }>(`${BASE}/checkout/cancel-bundle/`, propertyId ? { property_id: propertyId } : {});
    return response.data;
  },
};
